import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/ce/Group.svg';

const LoadingScreen = ({ onLoadingComplete }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + Math.random() * 25;
      });
    }, 70);

    const timer = setTimeout(() => {
      setIsLoading(false);
      if (onLoadingComplete) {
        setTimeout(onLoadingComplete, 400);
      }
    }, 1400);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, [onLoadingComplete]);

  const nameVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06,
        delayChildren: 0.3
      }
    }
  };

  const letterVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.8 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const name = "HARSH SANGHANI";

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="fixed inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800 z-50 flex flex-col items-center justify-center overflow-hidden"
        >
          {/* Sophisticated Background Patterns */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5 }}
              className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-red-900/20 via-red-800/10 to-transparent blur-3xl"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5, delay: 0.3 }}
              className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-tl from-red-800/15 to-transparent blur-3xl"
            />
          </div>

          <div className="relative z-10 flex flex-col items-center justify-center h-full px-6">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="mb-12"
            >
              <img 
                src={logo} 
                alt="Signature" 
                className="w-56 md:w-72 h-auto object-contain brightness-0 invert drop-shadow-2xl"
              />
            </motion.div>

            {/* Premium Typography */}
            <motion.div
              variants={nameVariants}
              initial="hidden"
              animate="visible"
              className="mb-12"
            >
              <div className="flex justify-center items-center flex-wrap gap-1">
                {name.split('').map((letter, index) => (
                  <motion.span
                    key={index}
                    variants={letterVariants}
                    className={`text-3xl md:text-4xl lg:text-5xl font-light text-white tracking-tight leading-none ${letter === ' ' ? 'w-3 md:w-5' : ''}`}
                    style={{ 
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      letterSpacing: letter === ' ' ? '0' : '0.02em'
                    }}
                  >
                    {letter === ' ' ? '\u00A0' : letter}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            {/* Elegant Progress */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="w-64 mx-auto"
            >
              <div className="relative">
                {/* Background Track */}
                <div className="w-full h-0.5 bg-gray-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-red-600 to-red-800 rounded-full relative"
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-red-500 rounded-full blur-sm opacity-80" />
                  </motion.div>
                </div>
                
                {/* Progress Text */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2, duration: 0.4 }}
                  className="flex justify-between items-center mt-3"
                >
                  <span className="text-xs font-medium text-gray-500 tracking-wide">
                    LOADING
                  </span>
                  <span className="text-xs font-mono text-gray-400 tabular-nums">
                    {Math.round(progress)}%
                  </span>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Corner Accents */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="absolute top-8 left-8 w-16 h-16 border-l border-t border-gray-700 rounded-tl-lg"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="absolute bottom-8 right-8 w-16 h-16 border-r border-b border-gray-700 rounded-br-lg"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;